import React, { useState } from 'react'
import { Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

interface OrderSearchBarProps {
	className?: string
	initialValue?: string
	onSearch?: (orderNumber: string) => void
}

const OrderSearchBar: React.FC<OrderSearchBarProps> = ({
	className = '',
	initialValue = '',
	onSearch
}) => {
	const [orderNumber, setOrderNumber] = useState(initialValue)
	const [error, setError] = useState<string>('')
	const navigate = useNavigate()

	const validateOrderNumber = (value: string): boolean => {
		// Accept standard and repair order numbers.
		const orderPattern = /^(ORDER|REPAIR)-\d{8}-\d{4}$/i
		return orderPattern.test(value.trim())
	}

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		const trimmedOrderNumber = orderNumber.trim().toUpperCase()

		if (!trimmedOrderNumber) {
			setError('Please enter an order number')
			return
		}

		if (!validateOrderNumber(trimmedOrderNumber)) {
			setError('Please enter a valid order number (e.g., ORDER-20260101-1234 or REPAIR-20260101-1234)')
			return
		}

		setError('')

		if (onSearch) {
			onSearch(trimmedOrderNumber)
		} else {
			navigate(`/track/${trimmedOrderNumber}`)
		}
	}

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setOrderNumber(e.target.value)
		if (error) setError('')
	}

	return (
		<div className={`w-full ${className}`}>
			<form onSubmit={handleSubmit} className="w-full">
				<div className="relative">
					<input
						id="tracking-order-number"
						aria-label="Order number"
						aria-invalid={Boolean(error)}
						aria-describedby={error ? 'tracking-search-error' : undefined}
						type="text"
						value={orderNumber}
						onChange={handleChange}
						placeholder="e.g. ORDER-20260101-1234"
						className={`w-full px-4 py-4 pr-12 text-base border-2 rounded-lg
							focus:outline-none focus:ring-2 focus:ring-orange focus:border-transparent
							transition-all duration-200
							${error ? 'border-red-400' : 'border-grey'}
						`}
					/>
					<button
						type="submit"
						className="absolute right-2 top-1/2 -translate-y-1/2 p-2
							bg-orange text-white rounded-md hover:bg-orange-600
							transition-colors duration-200"
						aria-label="Search order"
					>
						<Search className="w-5 h-5" />
					</button>
				</div>
				{error && (
					<p id="tracking-search-error" role="alert" className="mt-2 text-sm text-red-500">{error}</p>
				)}
			</form>
		</div>
	)
}

export default OrderSearchBar
